import { useState, useMemo } from 'react';
import ProgramLiveCard from './components/ProgramLiveCard';
import ProgramLiveSortBar from './components/ProgramLiveSortBar';
import { livePrograms, liveProgramsStats, type ProgramSortMode } from '@/mocks/livePrograms';
import { liveRadiosStats } from '@/mocks/liveRadios';

export default function ProgramsLivePage() {
  const [sortMode, setSortMode] = useState<ProgramSortMode>('activity');

  const sortedPrograms = useMemo(() => {
    const list = [...livePrograms];
    if (sortMode === 'activity') {
      list.sort((a, b) => b.activityScore - a.activityScore);
    } else if (sortMode === 'interactions') {
      list.sort((a, b) => b.interactions - a.interactions);
    } else {
      list.sort((a, b) => b.followers - a.followers);
    }
    return list;
  }, [sortMode]);

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[320px] md:h-[440px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dynamic%20radio%20production%20studio%20with%20multiple%20hosts%20and%20producers%20working%20together%2C%20large%20mixing%20console%20glowing%20with%20activity%2C%20multiple%20microphone%20booms%20across%20a%20long%20broadcast%20desk%2C%20warm%20amber%20studio%20lighting%20creating%20energetic%20atmosphere%2C%20professional%20broadcast%20production%20hub%20with%20behind%20the%20scenes%20energy%2C%20editorial%20photography%20with%20rich%20warm%20tones%20and%20cinematographic%20depth%2C%20collaborative%20creative%20radio%20production%20center&width=1600&height=900&seq=programs-live-hero-01&orientation=landscape"
          alt="Programas en Vivo Ahora"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
            <span className="text-[10px] font-extrabold text-background-50 tracking-widest uppercase bg-emerald-500/80 px-3 py-1 rounded-full backdrop-blur-sm">
              TRANSMITIENDO
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Programas en Vivo Ahora
          </h1>
          <p className="text-sm md:text-base text-background-50/75 max-w-lg leading-relaxed">
            Todos los programas que están al aire en este momento. Conectate con tu comunidad favorita y no te pierdas nada.
          </p>
          <div className="flex items-center gap-5 mt-5">
            <div className="flex items-center gap-2 text-background-50/90 text-xs">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="font-bold">{liveProgramsStats.totalPrograms} programas</span>
            </div>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveRadiosStats.totalLiveRadios} radios</span>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveRadiosStats.totalLiveListeners.toLocaleString()} oyentes</span>
          </div>
        </div>
      </section>

      {/* STATS ROW */}
      <section className="bg-background-100 py-5">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex flex-wrap gap-5 items-center justify-center">
          {[
            { icon: 'ri-mic-line', label: 'Programas al Aire', value: liveProgramsStats.totalPrograms.toString() },
            { icon: 'ri-group-line', label: 'Seguidores Totales', value: liveProgramsStats.totalFollowers >= 1000 ? `${(liveProgramsStats.totalFollowers / 1000).toFixed(1)}K` : liveProgramsStats.totalFollowers.toString() },
            { icon: 'ri-radio-line', label: 'Radios Activas', value: liveProgramsStats.totalRadios.toString() },
            { icon: 'ri-headphone-line', label: 'Oyentes Conectados', value: liveRadiosStats.totalLiveListeners.toLocaleString() },
            { icon: 'ri-chat-smile-2-line', label: 'Interacción Promedio', value: `${liveProgramsStats.avgInteractions}%` },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10 space-y-6">
        <ProgramLiveSortBar
          currentSort={sortMode}
          onSortChange={setSortMode}
          totalCount={sortedPrograms.length}
          totalListeners={liveRadiosStats.totalLiveListeners}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {sortedPrograms.map((program) => (
            <ProgramLiveCard key={program.id} program={program} />
          ))}
        </div>

        <section className="pt-8 pb-4 text-center">
          <p className="text-xs text-foreground-400">
            ¿Querés transmitir tu programa?{' '}
            <a href="/register-radio" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
              Registrá tu radio en RadioFrecuenciaWeb
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}